import { handleActions } from 'redux-actions'
import { uniqBy } from 'lodash'

import { JitsiTrack, JitsiLocalTrack, JitsiRemoteTrack } from 'lib-jitsi-meet'

import { VideoCallUser } from '@tabeeb/modules/../users/types'

import * as trackActions from '../actions/tracks'

export interface TracksState {
  localTracks: JitsiLocalTrack[]
  remoteTracks: { [userId: string]: JitsiRemoteTrack[] }
  previewTracks: JitsiTrack[]
  tracksError: string
  trackMuteInProcess: boolean
  largeVideoUser: VideoCallUser | null
  gridUser: VideoCallUser | null
  isPriorityView: boolean
}

const initialState: TracksState = {
  localTracks: [],
  remoteTracks: {},
  previewTracks: [],
  tracksError: '',
  trackMuteInProcess: false,
  largeVideoUser: null,
  gridUser: null,
  isPriorityView: true,
}

export default handleActions<TracksState, any>(
  {
    [trackActions.setLocalTracks.toString()](state, action: ReturnType<typeof trackActions.setLocalTracks>) {
      return { ...state, localTracks: action.payload }
    },
    [trackActions.updateLocalTrack.toString()](state, action: ReturnType<typeof trackActions.updateLocalTrack>) {
      const track = action.payload

      return {
        ...state,
        localTracks: uniqBy([track, ...state.localTracks], (t) => t.getType()),
      }
    },
    [trackActions.removeLocalTrack.toString()](state, action: ReturnType<typeof trackActions.removeLocalTrack>) {
      const track = action.payload
      const type = track.getType()

      return {
        ...state,
        localTracks: state.localTracks.filter((t) => t.getType() !== type),
      }
    },
    [trackActions.resetLocalTracks.toString()](state, action: ReturnType<typeof trackActions.resetLocalTracks>) {
      return { ...state, localTracks: [] }
    },
    [trackActions.addPreviewTrack.toString()](state, action: ReturnType<typeof trackActions.addPreviewTrack>) {
      const track = action.payload

      return {
        ...state,
        previewTracks: uniqBy([track, ...state.previewTracks], (t) => t.getType()),
      }
    },
    [trackActions.resetPreviewTracks.toString()](state) {
      return { ...state, previewTracks: [] }
    },
    [trackActions.setTracksError.toString()](state, action: ReturnType<typeof trackActions.setTracksError>) {
      return { ...state, tracksError: action.payload }
    },
    [trackActions.setTrackMuteState.toString()](state, action: ReturnType<typeof trackActions.setTrackMuteState>) {
      return { ...state, trackMuteInProcess: action.payload }
    },
    [trackActions.addRemoteTrack.toString()](state, action: ReturnType<typeof trackActions.addRemoteTrack>) {
      const { track, userId } = action.payload

      return {
        ...state,
        remoteTracks: {
          ...state.remoteTracks,
          [userId]: uniqBy([track, ...(state.remoteTracks[userId] ?? [])], (t) => t.getType()),
        },
      }
    },
    [trackActions.removeRemoteTrack.toString()](state, action: ReturnType<typeof trackActions.removeRemoteTrack>) {
      const { track, userId } = action.payload
      const type = track.getType()

      return {
        ...state,
        remoteTracks: {
          ...state.remoteTracks,
          [userId]: state.remoteTracks[userId]?.filter((t) => t.getType() !== type) ?? [],
        },
      }
    },
    [trackActions.resetTracksForUser.toString()](state, action: ReturnType<typeof trackActions.resetTracksForUser>) {
      const userId = action.payload

      return {
        ...state,
        remoteTracks: {
          ...state.remoteTracks,
          [userId]: [],
        },
      }
    },
    [trackActions.setLargeVideoUser.toString()](state, action: ReturnType<typeof trackActions.setLargeVideoUser>) {
      return { ...state, largeVideoUser: action.payload }
    },
    [trackActions.setGridUser.toString()](state, action: ReturnType<typeof trackActions.setGridUser>) {
      return { ...state, gridUser: action.payload }
    },
    [trackActions.disposeTracks.toString()]() {
      return {
        localTracks: [],
        remoteTracks: {},
        previewTracks: [],
        tracksError: '',
        trackMuteInProcess: false,
        largeVideoUser: null,
      }
    },
    [trackActions.initTracksForUser.toString()](state, action: ReturnType<typeof trackActions.initTracksForUser>) {
      return {
        ...state,
        remoteTracks: {
          ...state.remoteTracks,
          [action.payload]: [],
        },
      }
    },
    [trackActions.toggleMobilePriorityView.toString()](state, action) {
      return {
        ...state,
        isPriorityView: action.payload,
      } as any
    },
  },
  initialState
)
